import { isError } from '@allurion/utils';
import { captureException } from '@sentry/react';
import { Html5Qrcode } from 'html5-qrcode';

export class QRCodeReader {
  private static reader: Html5Qrcode | null = null;
  private static facingMode: 'environment' | 'user' = 'environment';

  static async init(containerId: string) {
    if (QRCodeReader.reader) {
      await QRCodeReader.stop();
    }

    QRCodeReader.reader = new Html5Qrcode(containerId);
  }

  static async start<T>({
    parse,
    onValid,
  }: {
    parse: (text: string) => T | false;
    onValid: (result: T) => Promise<void>;
  }) {
    const uniqueErrors = new Set<string>();
    const html5QrCode = QRCodeReader.reader;

    if (!html5QrCode) {
      return;
    }

    html5QrCode
      .start(
        { facingMode: QRCodeReader.facingMode },
        {
          fps: 10, // Optional, frame per seconds for qr code scanning
          qrbox: { width: 250, height: 250 }, // Optional, if you want bounded box UI
        },
        (decodedText: string) => {
          html5QrCode.pause(true);

          const parsed = parse(decodedText);

          if (!parsed) {
            html5QrCode.resume();

            return;
          }

          setTimeout(async () => {
            await onValid(parsed);
            QRCodeReader.stop();
          }, 1500);
        },
        (error) => {
          // parse error, ignore it.
          const message = isError(error) ? error.message : error;

          //Examples of errors:
          // QR code parse error, error = NotFoundException: No MultiFormat Readers were able to detect the code.
          // QR code parse error, error = No barcode or QR code detected.
          if (uniqueErrors.has(message)) {
            return;
          }

          uniqueErrors.add(message);
        }
      )
      .catch((error) => {
        captureException(error);
      });
  }

  static async stop() {
    const html5QrCode = QRCodeReader.reader;

    if (!html5QrCode) {
      return;
    }

    try {
      await html5QrCode.stop();
      await html5QrCode.clear();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // console.log('QRCodeReader.stop error', error);
    }

    QRCodeReader.reader = null;
  }

  static switchCameraFacingMode() {
    QRCodeReader.facingMode = QRCodeReader.facingMode === 'environment' ? 'user' : 'environment';
  }

  static async countCamerasAvailable() {
    const html5QrCode = QRCodeReader.reader;

    if (!html5QrCode) {
      return 0;
    }

    try {
      const devices = await Html5Qrcode.getCameras();

      return devices.length;
    } catch (error) {
      captureException(error);

      return 0;
    }
  }
}
